import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { clientEnv } from '@/lib/env';
import { learnerApiJson } from '@/lib/learner-api';
import { track } from '@/lib/analytics';
import { parsePaywallSource, paywallEventProps, type PaywallSource } from '@/lib/paywall';
import { typeScale } from '@/lib/typography';

type Plan = { lookup_key: 'monthly' | 'annual'; price_id: string; price_string: string; interval: string; amount: number; currency: string };
type Plans = { plans: Plan[] };
type Checkout = { checkout_url: string };

const SHOW_ANNUAL_PLAN = false;
const VISIBLE_PLANS = (['annual', 'monthly'] as const).filter(
  (key) => key === 'monthly' || SHOW_ANNUAL_PLAN,
);

type SubscribeSearch = {
  source?: PaywallSource;
  content?: string;
  checkout_success?: string;
};

export const Route = createFileRoute('/app/subscribe')({
  component: SubscribeRoute,
  validateSearch: (search: Record<string, unknown>): SubscribeSearch => ({
    source: parsePaywallSource(search.source),
    content: typeof search.content === 'string' ? search.content : undefined,
    checkout_success: typeof search.checkout_success === 'string' ? search.checkout_success : undefined,
  }),
});

function SubscribeRoute() {
  if (!clientEnv.clerkPublishableKey) return <SignInRequired />;
  return <CheckoutPaywall />;
}

function SignInRequired() {
  return (
    <main className="grid min-h-[80vh] place-items-center p-5 text-center">
      <div>
        <h1 className={typeScale.screenTitle}>Sign in to subscribe</h1>
        <p className="mt-3 text-slate-600">Your subscription will work on web, iPhone, and Android.</p>
        <Link to="/auth/sign-in" className="mt-6 inline-block rounded-2xl bg-[#399918] px-6 py-3 font-bold text-white">
          Sign in
        </Link>
      </div>
    </main>
  );
}

function CheckoutPaywall() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const search = Route.useSearch();
  const source = search.source ?? 'settings';
  const [selected, setSelected] = useState<'annual' | 'monthly'>('monthly');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const plans = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: () => learnerApiJson<Plans>(getToken, '/v1/subscription/plans', { auth: true }),
    enabled: Boolean(isSignedIn),
  });

  useEffect(() => {
    track(
      'Paywall Screen',
      paywallEventProps({
        source,
        surface: 'marketing',
        content: search.content,
        signed_in: isSignedIn,
      }),
    );
    if (search.checkout_success !== 'true' || !isSignedIn) return;
    window.history.replaceState({}, '', window.location.pathname);
    let stopped = false;
    const verify = async () => {
      setMessage('Payment received. Syncing Premium access…');
      for (let attempt = 0; attempt < 4 && !stopped; attempt += 1) {
        try {
          const status = await learnerApiJson<{ subscribed: boolean }>(getToken, '/v1/subscription/verify', { method: 'POST', auth: true });
          if (status.subscribed) {
            setMessage('Premium is active. Welcome!');
            track('Checkout Success', { store: 'stripe' });
            return;
          }
        } catch {
          // Bounded retry handles webhook/import propagation.
        }
        await new Promise((resolve) => window.setTimeout(resolve, 900 * (attempt + 1)));
      }
      if (!stopped) setMessage('Payment is still syncing. Refresh in a moment—please do not purchase again.');
    };
    void verify();
    return () => { stopped = true; };
  }, [getToken, isSignedIn, search.checkout_success, search.content, source]);

  if (isLoaded && !isSignedIn) return <SignInRequired />;
  const selectedPlan = plans.data?.plans.find((plan) => plan.lookup_key === selected);
  const purchase = async () => {
    if (!selectedPlan || busy) return;
    setBusy(true);
    setMessage('');
    track(
      'Paywall Purchase Press',
      paywallEventProps(
        { source, surface: 'marketing', content: search.content, signed_in: isSignedIn },
        { plan: selected },
      ),
    );
    try {
      const checkout = await learnerApiJson<Checkout>(getToken, '/v1/subscription/create-checkout-session', {
        method: 'POST',
        auth: true,
        body: JSON.stringify({ plan: selected, attempt_id: crypto.randomUUID() }),
      });
      window.location.assign(checkout.checkout_url);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Checkout could not start. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8">
      <div className="text-center">
        <p className="text-sm font-bold uppercase tracking-[.2em] text-[#399918]">Hmonglus Premium</p>
        <h1 className={`mt-3 ${typeScale.paywallTitle}`}>Learn without limits.</h1>
      </div>
      <div className={`mt-8 grid gap-4 ${VISIBLE_PLANS.length > 1 ? 'sm:grid-cols-2' : ''}`}>
        {VISIBLE_PLANS.map((key) => {
          const plan = plans.data?.plans.find((candidate) => candidate.lookup_key === key);
          return <button key={key} onClick={() => setSelected(key)} className={`rounded-3xl border-2 bg-white p-6 text-left ${selected === key ? 'border-[#399918] ring-4 ring-[#399918]/10' : 'border-[#cfe8c6]'}`}><span className="font-extrabold">{key === 'annual' ? 'Annual' : 'Monthly'}</span>{key === 'annual' && <span className="ml-2 rounded-full bg-[#e5f3e7] px-2 py-1 text-xs font-bold text-[#276e17]">Best value</span>}<p className="mt-3 text-2xl font-black">{plan?.price_string ?? '—'}</p><p className="text-sm text-slate-500">billed {key === 'annual' ? 'yearly' : 'monthly'}</p></button>;
        })}
      </div>
      <ul className="mx-auto mt-8 max-w-lg space-y-3">{['Crossword archive and premium puzzles', 'Premium stories and lessons', 'Nab and premium sticker packs', 'Access across web, iPhone, and Android'].map((feature) => <li key={feature} className="flex gap-3"><span className="text-[#399918]">✓</span><span>{feature}</span></li>)}</ul>
      {message && <p role="status" className="mt-6 rounded-2xl bg-white p-4 text-center text-sm">{message}</p>}
      <button onClick={() => void purchase()} disabled={!selectedPlan || busy} className="mt-7 w-full rounded-2xl bg-[#399918] px-6 py-4 text-lg font-extrabold text-white disabled:opacity-50">{busy ? 'Opening secure checkout…' : 'Continue with Stripe'}</button>
      <p className="mt-4 text-center text-xs leading-5 text-slate-500">Secure payment by Stripe. By continuing, you agree to the <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
    </div>
  );
}
